import { useState } from 'react';
import {
  Link2,
  Unlink,
  RefreshCw,
  Trash2,
  Building2,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowDownToLine,
  ExternalLink,
  ShieldCheck,
  Plus,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import PageHeader from '@components/layout/PageHeader';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import Card from '@components/common/Card';
import Badge from '@components/common/Badge';
import { useOpenFinance } from '@hooks/useOpenFinance';
import { useAccounts } from '@hooks/useAccounts';
import { useAuth } from '@hooks/useAuth';
import { formatDate } from '@utils/formatters';

// ─── Status helpers ──────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  active: { label: 'Ativo', variant: 'success', icon: CheckCircle2 },
  pending: { label: 'Pendente', variant: 'warning', icon: Clock },
  error: { label: 'Erro', variant: 'danger', icon: AlertCircle },
  expired: { label: 'Expirado', variant: 'danger', icon: AlertCircle },
  revoked: { label: 'Revogado', variant: 'default', icon: Unlink },
};

const getStatusConfig = (status) =>
  STATUS_CONFIG[status] || { label: status, variant: 'default', icon: Clock };

// ─── MetricCard ──────────────────────────────────────────────────────────────

const MetricCard = ({ title, value, subtitle, icon, iconBg, valueColor }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</p>
      <div className={clsx('p-2.5 rounded-xl', iconBg)}>{icon}</div>
    </div>
    <p className={clsx('text-2xl font-bold', valueColor)}>{value}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

// ─── ConnectionCard ──────────────────────────────────────────────────────────

const ConnectionCard = ({
  connection,
  onSyncAccounts,
  onSyncTransactions,
  onLinkAccount,
  onDelete,
  onReconnect,
  isSyncingAccounts,
  isSyncingTransactions,
}) => {
  const statusCfg = getStatusConfig(connection.status);
  const StatusIcon = statusCfg.icon;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      {/* Color accent bar */}
      <div
        className={clsx(
          'h-1.5',
          connection.status === 'active' && 'bg-emerald-500',
          connection.status === 'pending' && 'bg-yellow-500',
          connection.status === 'error' && 'bg-red-500',
          connection.status === 'expired' && 'bg-red-400',
          connection.status === 'revoked' && 'bg-gray-400',
          !STATUS_CONFIG[connection.status] && 'bg-gray-300'
        )}
      />

      <div className="p-5">
        {/* Header: Logo + Name + Status */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {connection.institutionLogo ? (
              <img
                src={connection.institutionLogo}
                alt={connection.institutionName}
                className="w-12 h-12 rounded-xl object-contain bg-gray-50 dark:bg-gray-700 p-1"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <Building2 size={24} className="text-primary-600 dark:text-primary-400" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white leading-tight">
                {connection.institutionName || 'Banco'}
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                ID: {connection.connectionId?.slice(0, 8)}...
              </p>
            </div>
          </div>

          <Badge variant={statusCfg.variant} size="sm" dot>
            {statusCfg.label}
          </Badge>
        </div>

        {/* Info rows */}
        <div className="space-y-2 mb-4">
          {connection.lastSyncAt && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <RefreshCw size={14} className="flex-shrink-0" />
              <span>Ultima sinc.: {formatDate(connection.lastSyncAt, 'medium')}</span>
            </div>
          )}

          {connection.linkedAccountName ? (
            <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
              <Link2 size={14} className="flex-shrink-0" />
              <span>Vinculada a: {connection.linkedAccountName}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
              <Unlink size={14} className="flex-shrink-0" />
              <span>Nenhuma conta vinculada</span>
            </div>
          )}

          {connection.bankAccountsCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Building2 size={14} className="flex-shrink-0" />
              <span>{connection.bankAccountsCount} conta{connection.bankAccountsCount !== 1 ? 's' : ''} encontrada{connection.bankAccountsCount !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
          {(connection.status === 'expired' || connection.status === 'error') ? (
            <Button
              size="sm"
              variant="primary"
              leftIcon={<RefreshCw size={14} />}
              onClick={onReconnect}
              className="flex-1"
            >
              Reconectar
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<RefreshCw size={14} className={isSyncingAccounts ? 'animate-spin' : ''} />}
                onClick={() => onSyncAccounts(connection.connectionId)}
                disabled={isSyncingAccounts || connection.status !== 'active'}
              >
                Sincronizar
              </Button>

              <Button
                size="sm"
                variant="secondary"
                leftIcon={<ArrowDownToLine size={14} className={isSyncingTransactions ? 'animate-spin' : ''} />}
                onClick={() => onSyncTransactions(connection)}
                disabled={isSyncingTransactions || connection.status !== 'active'}
              >
                Importar
              </Button>

              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Link2 size={14} />}
                onClick={() => onLinkAccount(connection)}
                disabled={connection.status !== 'active'}
              >
                Vincular
              </Button>
            </>
          )}

          <Button
            size="sm"
            variant="danger"
            leftIcon={<Trash2 size={14} />}
            onClick={() => onDelete(connection)}
          >
            Remover
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── OpenFinance Page ────────────────────────────────────────────────────────

const OpenFinance = () => {
  const { hasFeature, tenant } = useAuth();
  const {
    connections,
    activeConnections,
    isLoading,
    createConnectToken,
    processConnection,
    syncAccounts,
    syncTransactions,
    linkAccount,
    deleteConnection,
    isCreatingToken,
    isProcessing,
    isSyncingAccounts,
    isSyncingTransactions,
    isDeleting,
  } = useOpenFinance();

  const { accounts } = useAccounts();

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [syncDays, setSyncDays] = useState(30);

  // ─── Feature gate ──────────────────────────────────────────────────────────

  const isPro = hasFeature('openFinance') || hasFeature('investments');

  if (!isPro) {
    return (
      <div className="space-y-6 pb-8">
        <PageHeader
          title="Open Finance"
          subtitle="Conecte seus bancos e importe transacoes automaticamente"
        />

        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <div className="w-20 h-20 rounded-2xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-6">
            <ShieldCheck size={40} className="text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Recurso Exclusivo Pro
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mb-6">
            A conexao com Open Finance esta disponivel nos planos Pro e Family.
            Conecte seus bancos e importe transacoes automaticamente.
          </p>
          <Button
            leftIcon={<ExternalLink size={18} />}
            onClick={() => window.location.href = '/app/settings/billing'}
          >
            Conhecer Planos
          </Button>
        </div>
      </div>
    );
  }

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleConnectBank = async () => {
    try {
      const { accessToken } = await createConnectToken();
      // Open Pluggy Connect widget
      // Reutiliza o script se já carregado
      const pluggyOptions = {
        connectToken: accessToken,
        includeSandbox: true,
        onSuccess: async (data) => {
          await processConnection({ itemId: data.item.id });
        },
        onError: (error) => {
          console.error('Pluggy Connect error:', error);
          toast.error('Erro ao conectar banco');
        },
      };

      if (window.PluggyConnect) {
        new window.PluggyConnect(pluggyOptions).init();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.pluggy.ai/pluggy-connect/latest/pluggy-connect.js';
      script.onload = () => {
        new window.PluggyConnect(pluggyOptions).init();
      };
      script.onerror = () => {
        toast.error('Não foi possível carregar o widget de conexão bancária. Tente novamente mais tarde.');
      };
      document.head.appendChild(script);
    } catch (err) {
      // error handled by mutation
    }
  };

  const handleSyncAccounts = async (connectionId) => {
    await syncAccounts(connectionId);
  };

  const openSyncTransactionsModal = (connection) => {
    setSelectedConnection(connection);
    setSyncDays(30);
    setIsSyncModalOpen(true);
  };

  const handleSyncTransactions = async () => {
    if (!selectedConnection) return;
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - syncDays);

    await syncTransactions({
      connectionId: selectedConnection.connectionId,
      data: {
        startDate: startDate.toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        ...(selectedConnection.linkedAccountId ? { linkedAccountId: selectedConnection.linkedAccountId } : {}),
      },
    });
    setIsSyncModalOpen(false);
    setSelectedConnection(null);
  };

  const openLinkModal = (connection) => {
    setSelectedConnection(connection);
    setSelectedAccountId(connection.linkedAccountId || '');
    setIsLinkModalOpen(true);
  };

  const handleLinkAccount = async () => {
    if (!selectedConnection || !selectedAccountId) return;
    await linkAccount({
      connectionId: selectedConnection.connectionId,
      accountId: selectedAccountId,
    });
    setIsLinkModalOpen(false);
    setSelectedConnection(null);
    setSelectedAccountId('');
  };

  const openDeleteModal = (connection) => {
    setSelectedConnection(connection);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedConnection) return;
    await deleteConnection(selectedConnection.connectionId);
    setIsDeleteModalOpen(false);
    setSelectedConnection(null);
  };

  // ─── Computed ──────────────────────────────────────────────────────────────

  const lastSync = connections.reduce((latest, c) => {
    if (!c.lastSyncAt) return latest;
    if (!latest) return c.lastSyncAt;
    return new Date(c.lastSyncAt) > new Date(latest) ? c.lastSyncAt : latest;
  }, null);

  // ─── Loading ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return <Loading text="Carregando conexoes..." />;
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Open Finance"
        subtitle="Conecte seus bancos e importe transacoes automaticamente"
        action={
          <Button
            leftIcon={<Plus size={18} />}
            onClick={handleConnectBank}
            isLoading={isCreatingToken || isProcessing}
          >
            Conectar Banco
          </Button>
        }
      />

      {/* Hero Summary */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-primary-900/80 to-slate-900 text-white shadow-2xl p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-primary-500/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary-400/10 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-white/50 text-sm font-medium mb-1">Open Finance</p>
            <p className="text-4xl font-bold tracking-tight text-white">
              {connections.length}
            </p>
            <p className="text-white/40 text-sm mt-2">
              conexo{connections.length !== 1 ? 'es' : ''} cadastrada{connections.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-6">
            <div className="text-center">
              <p className="text-white/40 text-xs mb-1">Ativas</p>
              <p className="text-emerald-400 font-bold text-lg">{activeConnections.length}</p>
            </div>
            <div className="w-px bg-white/10" />
            <div className="text-center">
              <p className="text-white/40 text-xs mb-1">Ultima Sinc.</p>
              <p className="text-white font-bold text-sm">
                {lastSync ? formatDate(lastSync, 'medium') : 'Nunca'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total de Conexoes"
          value={connections.length}
          subtitle={`${activeConnections.length} ativa${activeConnections.length !== 1 ? 's' : ''}`}
          icon={<Building2 size={18} className="text-primary-600" />}
          iconBg="bg-primary-100 dark:bg-primary-900/30"
          valueColor="text-gray-900 dark:text-white"
        />
        <MetricCard
          title="Conexoes Ativas"
          value={activeConnections.length}
          icon={<CheckCircle2 size={18} className="text-emerald-600" />}
          iconBg="bg-emerald-100 dark:bg-emerald-900/30"
          valueColor="text-emerald-600"
        />
        <MetricCard
          title="Contas Vinculadas"
          value={connections.filter(c => c.linkedAccountId).length}
          subtitle={`de ${connections.length} conexo${connections.length !== 1 ? 'es' : ''}`}
          icon={<Link2 size={18} className="text-blue-600" />}
          iconBg="bg-blue-100 dark:bg-blue-900/30"
          valueColor="text-blue-600"
        />
        <MetricCard
          title="Ultima Sincronizacao"
          value={lastSync ? formatDate(lastSync, 'dayMonth') : '--'}
          subtitle={lastSync ? 'Dados atualizados' : 'Nenhuma sinc. realizada'}
          icon={<RefreshCw size={18} className="text-purple-600" />}
          iconBg="bg-purple-100 dark:bg-purple-900/30"
          valueColor="text-purple-600"
        />
      </div>

      {/* Security notice */}
      <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 rounded-xl">
        <ShieldCheck size={20} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
        <p className="text-sm text-emerald-700 dark:text-emerald-300">
          Suas conexoes sao protegidas por criptografia de ponta a ponta. O MyBank nao armazena suas credenciais bancarias.
        </p>
      </div>

      {/* Connections Grid */}
      {connections.length === 0 ? (
        <EmptyState
          icon={<Building2 size={32} />}
          title="Nenhum banco conectado"
          description="Conecte seu banco para importar transacoes automaticamente via Open Finance."
          action={handleConnectBank}
          actionLabel="Conectar Banco"
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {connections.map((connection) => (
            <ConnectionCard
              key={connection.connectionId}
              connection={connection}
              onSyncAccounts={handleSyncAccounts}
              onSyncTransactions={openSyncTransactionsModal}
              onLinkAccount={openLinkModal}
              onDelete={openDeleteModal}
              onReconnect={handleConnectBank}
              isSyncingAccounts={isSyncingAccounts}
              isSyncingTransactions={isSyncingTransactions}
            />
          ))}

          {/* Add new connection card */}
          <button
            onClick={handleConnectBank}
            disabled={isCreatingToken || isProcessing}
            className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-5 flex flex-col items-center justify-center gap-2 hover:border-primary-400 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 transition-all group min-h-[220px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 flex items-center justify-center transition-colors">
              <Plus size={24} className="text-gray-400 group-hover:text-primary-600 transition-colors" />
            </div>
            <p className="text-sm font-medium text-gray-400 group-hover:text-primary-600 transition-colors">
              Conectar Novo Banco
            </p>
          </button>
        </div>
      )}

      {/* ─── Link Account Modal ─────────────────────────────────────────────── */}

      <Modal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        title="Vincular Conta"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsLinkModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleLinkAccount}
              isLoading={false}
              disabled={!selectedAccountId}
            >
              Vincular
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Selecione a conta do MyBank que deseja vincular a conexao com{' '}
            <strong>{selectedConnection?.institutionName}</strong>.
            As transacoes importadas serao registradas nesta conta.
          </p>

          {accounts.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Voce ainda nao tem contas cadastradas.
              </p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-2"
                onClick={() => {
                  setIsLinkModalOpen(false);
                  window.location.href = '/app/accounts';
                }}
              >
                Criar Conta
              </Button>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {accounts.map((account) => (
                <label
                  key={account.accountId}
                  className={clsx(
                    'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all',
                    selectedAccountId === account.accountId
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  )}
                >
                  <input
                    type="radio"
                    name="linkAccount"
                    value={account.accountId}
                    checked={selectedAccountId === account.accountId}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-lg flex-shrink-0"
                    style={{ backgroundColor: `${account.color}18` }}
                  >
                    {account.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                      {account.name}
                    </p>
                    <p className="text-xs text-gray-400">{account.type}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* ─── Sync Transactions Modal ────────────────────────────────────────── */}

      <Modal
        isOpen={isSyncModalOpen}
        onClose={() => setIsSyncModalOpen(false)}
        title="Importar Transacoes"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsSyncModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSyncTransactions}
              isLoading={isSyncingTransactions}
              leftIcon={<ArrowDownToLine size={16} />}
            >
              Importar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Escolha o periodo para importar transacoes de{' '}
            <strong>{selectedConnection?.institutionName}</strong>.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Periodo
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 7, label: '7 dias' },
                { value: 15, label: '15 dias' },
                { value: 30, label: '30 dias' },
                { value: 60, label: '60 dias' },
                { value: 90, label: '90 dias' },
                { value: 180, label: '6 meses' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSyncDays(option.value)}
                  className={clsx(
                    'px-3 py-2 rounded-lg text-sm font-medium border transition-all',
                    syncDays === option.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 dark:border-primary-600'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {!selectedConnection?.linkedAccountId && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <AlertCircle size={16} className="text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                Esta conexao nao possui conta vinculada. As transacoes serao importadas mas nao vinculadas a nenhuma conta.
                Vincule uma conta para melhor organizacao.
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* ─── Delete Confirmation Modal ──────────────────────────────────────── */}

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Remover Conexao"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isDeleting}>
              Remover
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-gray-600 dark:text-gray-400">
            Tem certeza que deseja remover a conexao com{' '}
            <strong>{selectedConnection?.institutionName}</strong>?
          </p>
          <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-300">
              Esta acao revogara o acesso do MyBank aos dados deste banco.
              As transacoes ja importadas nao serao afetadas.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OpenFinance;
